/**
 * Sales order hooks (S2.2) — house list stack + store-first mutations.
 *
 * - `useSalesOrdersTable` drives the rebuilt SalesOrderListPage via
 *   useAntdTable (pre-first-fetch `loading || params.length === 0` guard,
 *   store echo). Status / priceStatus / search / single-customer filters go
 *   server-side; multi-customer and the orderDate recency buckets union
 *   client-side over a mock-scale fetch so pagination totals stay correct
 *   (same pattern as useProductsTable's multi-category union).
 * - Reads use `/api/`-prefixed cacheKeys (dynamic ones tracked through the
 *   cacheKeyRegistry); every mutation invalidates via exact-key clears —
 *   the ahooks `clearCache(predicate)` form is a silent no-op.
 * - `useReadyProducts` is the S2.2 line-editor picker feed: Ready + active
 *   products ONLY (non-Ready products are ABSENT from the picker, not
 *   disabled — the two-tier readiness gate).
 */

import { clearCache, useAntdTable, useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { salesOrderApi } from '@/services/api';
import { useProductOptions } from '@/hooks/useProducts';
import { useSoStore } from '@/stores/soStore';
import type { SoFilters } from '@/stores/soStore';
import { clearCacheWhere, trackCacheKey } from '@/utils/cacheKeyRegistry';
import type { PriceLevel } from '@/types/api/pricing';
import type {
  CreateSalesOrderRequest,
  SalesOrder,
  SalesOrderListParams,
} from '@/types/api/salesOrder';

// ---------------------------------------------------------------------------
// Cache keys (static, /api/-prefixed — house rule)
// ---------------------------------------------------------------------------

export const soDetailCacheKey = (id: string) => `/api/sales-orders/${id}`;
const SO_PRICE_LEVELS_CACHE_KEY = '/api/pricing/levels';
const soCustomerPricingCacheKey = (customerId: number) =>
  `/api/pricing/customer-pricing?customerId=${customerId}`;

/** Invalidate every cache a sales-order mutation can stale (exact keys only). */
export function invalidateSalesOrderCaches(...soIds: Array<string | undefined>) {
  const detailKeys = soIds
    .filter((id): id is string => Boolean(id))
    .map((id) => soDetailCacheKey(id));
  if (detailKeys.length > 0) clearCache(detailKeys);
  clearCacheWhere(
    (key) =>
      key.startsWith('/api/sales-orders') ||
      key.startsWith('/api/purchase-orders') ||
      key.startsWith('/api/occ')
  );
  // Static lens keys registered by other hooks (literal cacheKey strings).
  clearCache([
    '/api/sales-orders?view=product-transactions',
    '/api/purchase-orders?view=product-transactions',
  ]);
}

// ---------------------------------------------------------------------------
// List (useAntdTable — S0.3 list-kit contract)
// ---------------------------------------------------------------------------

/** Mock-scale ceiling for the client-side union fetch. */
const UNION_FETCH_PAGE_SIZE = 500;

const DATE_RANGE_DAYS: Record<Exclude<SoFilters['dateRange'], 'all'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

export function useSalesOrdersTable(filters: SoFilters) {
  const { setSalesOrders, setPagination } = useSoStore();

  const needsClientFilter = filters.customerIds.length > 1 || filters.dateRange !== 'all';

  const baseParams = (): SalesOrderListParams => ({
    search: filters.search.trim() || undefined,
    status: filters.status === 'all' ? undefined : filters.status,
    priceStatus: filters.priceStatus === 'all' ? undefined : filters.priceStatus,
  });

  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize }) => {
      if (!needsClientFilter) {
        const response = await salesOrderApi.list({
          ...baseParams(),
          customerId: filters.customerIds[0],
          page: current,
          pageSize,
        });
        setSalesOrders(response.data);
        setPagination(response.pagination);
        return { total: response.pagination.total, list: response.data };
      }

      // Multi-customer and/or date bucket: union client-side over a
      // mock-scale fetch so pagination totals stay correct.
      const response = await salesOrderApi.list({
        ...baseParams(),
        page: 1,
        pageSize: UNION_FETCH_PAGE_SIZE,
      });
      let rows = response.data;
      if (filters.customerIds.length > 0) {
        const allowed = new Set(filters.customerIds);
        rows = rows.filter((so) => allowed.has(so.customerId));
      }
      if (filters.dateRange !== 'all') {
        const cutoff = dayjs()
          .subtract(DATE_RANGE_DAYS[filters.dateRange], 'day')
          .format('YYYY-MM-DD');
        rows = rows.filter((so) => so.orderDate >= cutoff);
      }
      const start = (current - 1) * pageSize;
      const pageRows = rows.slice(start, start + pageSize);
      setSalesOrders(pageRows);
      setPagination({
        page: current,
        pageSize,
        total: rows.length,
        totalPages: Math.ceil(rows.length / pageSize) || 1,
      });
      return { total: rows.length, list: pageRows };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [
        filters.search,
        filters.status,
        filters.priceStatus,
        JSON.stringify(filters.customerIds),
        filters.dateRange,
      ],
    }
  );

  return {
    tableProps,
    refresh,
    // Pre-first-fetch guard (house rule) — never flash the empty state
    isLoading: loading || params.length === 0,
    error,
  };
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

export function useSalesOrder(id?: string) {
  const { data, error, loading, run } = useRequest(() => salesOrderApi.getById(id!), {
    ready: !!id,
    refreshDeps: [id],
    cacheKey: id ? trackCacheKey(soDetailCacheKey(id)) : undefined,
  });

  return {
    salesOrder: data?.data,
    error,
    isLoading: loading,
    /** Invalidate + re-run (never bare ahooks refresh in components). */
    refresh: () => {
      if (id) invalidateSalesOrderCaches(id);
      return run();
    },
  };
}

// ---------------------------------------------------------------------------
// Ready-product picker feed (the two-tier readiness gate)
// ---------------------------------------------------------------------------

export function useReadyProducts() {
  const { products, error, isLoading } = useProductOptions('ready');

  return { products, error, isLoading };
}

// ---------------------------------------------------------------------------
// Customer price level (the S2.2 price-level chip)
// ---------------------------------------------------------------------------

export interface CustomerPriceLevelInfo {
  /** Resolved PriceLevel row; null = no level assigned (falls to List Price). */
  priceLevel: PriceLevel | null;
  /** Optional post-level customer discount percent (5 = −5%). */
  discountPct: number | null;
  isLoading: boolean;
}

export function useCustomerPriceLevel(customerId?: number | null): CustomerPriceLevelInfo {
  const levels = useRequest(() => salesOrderApi.listPriceLevels(), {
    cacheKey: SO_PRICE_LEVELS_CACHE_KEY,
  });
  const pricing = useRequest(() => salesOrderApi.listCustomerPricing(customerId!), {
    ready: customerId != null,
    refreshDeps: [customerId],
    cacheKey:
      customerId != null ? trackCacheKey(soCustomerPricingCacheKey(customerId)) : undefined,
  });

  const assignment = pricing.data?.data?.[0] ?? null;
  const priceLevel =
    assignment?.priceLevelId != null
      ? (levels.data?.data ?? []).find((level) => level.id === assignment.priceLevelId) ?? null
      : null;

  return {
    priceLevel,
    discountPct: assignment?.discountPct ?? null,
    isLoading: levels.loading || pricing.loading,
  };
}

// ---------------------------------------------------------------------------
// Mutations (store-first: update Zustand echo + exact-key clearCache; the
// calling surface re-runs its own wrapped refresh — never bare refresh())
// ---------------------------------------------------------------------------

/** Shape of the mock API error envelope (axios error response body). */
export interface ApiErrorBody {
  error?: string;
  message?: string;
}

export function extractApiError(error: unknown): ApiErrorBody {
  const err = error as { response?: { data?: ApiErrorBody } };
  return err?.response?.data ?? {};
}

export function useSalesOrderMutations() {
  const { upsertSalesOrderInList } = useSoStore();

  const createSalesOrder = async (body: CreateSalesOrderRequest): Promise<SalesOrder> => {
    const response = await salesOrderApi.create(body);
    upsertSalesOrderInList(response.data);
    invalidateSalesOrderCaches(response.data.id);
    return response.data;
  };

  const createPoFromSo = async (soId: string) => {
    const response = await salesOrderApi.createPo(soId);
    invalidateSalesOrderCaches(soId);
    return response;
  };

  return { createSalesOrder, createPoFromSo };
}
